// blocks/scroll-jacking.js
import { loadGSAPWithScrollTrigger } from '../utils/utilities.js';

const SELECTORS = {
    section_wrapper: '.wp-block-ssm-section-wrapper',
    block: '.wp-block-ssm-scroll-jacking',
    track: '.wp-block-ssm-scroll-jacking__track',
    pin: '.wp-block-ssm-scroll-jacking__pin',
    slide: '.wp-block-ssm-scroll-jacking__slide',
};

const MOBILE_BREAKPOINT = 1024;

/**
 * Initialize ScrollJacking with dynamic GSAP loading
 */
export const ScrollJacking = async () => {
    const blocks = document.querySelectorAll(SELECTORS.block);

    if (blocks.length === 0) {
        console.log('ScrollJacking: No blocks found, skipping initialization');

        return;
    }

    try {
        // Dynamically load GSAP and ScrollTrigger if not already loaded
        if (!window.gsap || !window.ScrollTrigger) {
            await loadGSAPWithScrollTrigger();

            // Wait for next tick to ensure GSAP is fully attached to window
            await new Promise((resolve) => setTimeout(resolve, 0));
        }

        const gsap = window.gsap;
        const ScrollTrigger = window.ScrollTrigger;

        if (!gsap || !ScrollTrigger) {
            console.error('ScrollJacking: Failed to load GSAP or ScrollTrigger');

            return;
        }

        // Register plugin BEFORE dispatching event
        gsap.registerPlugin(ScrollTrigger);

        // NOW notify that GSAP is available (triggers Lenis-ScrollTrigger integration)
        window.dispatchEvent(new CustomEvent('gsap:loaded'));

        // Initialize all ScrollJacking blocks
        blocks.forEach((block) => {
            try {
                initScrollJacking(block, gsap, ScrollTrigger);
            } catch (error) {
                console.error('ScrollJacking: Error initializing block:', error, block);
            }
        });
    } catch (error) {
        console.error('ScrollJacking: Failed to load dependencies:', error);
    }
};

/**
 * Initialize a single ScrollJacking block
 */
export const initScrollJacking = (block, gsap, ScrollTrigger, isEditor = false) => {
    if (!block) {
        console.error('ScrollJacking: Invalid block element provided');

        return null;
    }

    if (!gsap || !ScrollTrigger) {
        console.error('ScrollJacking: GSAP or ScrollTrigger not available');

        return null;
    }

    const track = block.querySelector(SELECTORS.track);
    const section = block.closest(SELECTORS.section_wrapper);
    const slides = block.querySelectorAll(SELECTORS.slide);

    // Validate required elements
    if (!track) {
        console.warn('ScrollJacking: Track element not found in block', block);

        return null;
    }

    if (!section) {
        console.warn('ScrollJacking: Section wrapper not found for block', block);

        return null;
    }

    if (!slides || slides.length === 0) {
        console.warn('ScrollJacking: No slides found in block', block);

        return null;
    }

    let mm = gsap.matchMedia();
    let scrollerEl = document.scrollingElement;

    if (isEditor) {
        const topDoc = window.top?.document;
        const iframe = topDoc?.querySelector('iframe[name="editor-canvas"]');
        const iWin = iframe?.contentWindow;
        const iDoc = iframe?.contentDocument;
        scrollerEl = iDoc?.scrollingElement || scrollerEl;

        if (iWin && iDoc) {
            ScrollTrigger.scrollerProxy(scrollerEl, {
                scrollTop(v) {
                    if (arguments.length) scrollerEl.scrollTop = v;

                    return scrollerEl.scrollTop;
                },
                scrollLeft(v) {
                    if (arguments.length) scrollerEl.scrollLeft = v;

                    return scrollerEl.scrollLeft;
                },
                getBoundingClientRect() {
                    return {
                        top: 0,
                        left: 0,
                        width: iWin.innerWidth,
                        height: iWin.innerHeight,
                    };
                },
                pinType: 'fixed',
            });
        }
    }

    // Desktop behavior
    mm.add(`(min-width: ${MOBILE_BREAKPOINT}px)`, () => {
        let scrollTriggers = [];
        let cachedTrackHeight = 0;
        let cachedLastSlideHeight = 0;

        function updatePaddingValues() {
            try {
                const computedStyle = getComputedStyle(section);
                const pt = parseFloat(computedStyle.paddingTop) || 0;
                const pb = parseFloat(computedStyle.paddingBottom) || 0;

                section.style.setProperty('--section-pt', `${pt}px`);
                section.style.setProperty('--section-pb', `${pb}px`);
            } catch (error) {
                console.error('ScrollJacking: Error updating padding values:', error);
            }
        }

        function cacheHeights() {
            cachedTrackHeight = track.offsetHeight;
            const lastSlide = slides[slides.length - 1];
            cachedLastSlideHeight = lastSlide?.offsetHeight || 0;
        }

        function createAnimation() {
            try {
                updatePaddingValues();
                cacheHeights();

                if (!cachedTrackHeight) {
                    console.warn('ScrollJacking: Track has no height, skipping animation');

                    return;
                }

                slides.forEach((slide, i) => {
                    if (!slide) {
                        console.warn(`ScrollJacking: Slide at index ${i} is invalid`);

                        return;
                    }

                    const animation = gsap.to(slide, {
                        scrollTrigger: {
                            scroller: scrollerEl,
                            trigger: slide,
                            start: 'center center',
                            end: () => `${cachedTrackHeight - cachedLastSlideHeight / 2}px center`,
                            endTrigger: track,
                            scrub: 1,
                            pin: slide,
                            pinSpacing: false,
                            invalidateOnRefresh: true,
                            anticipatePin: 1,
                            fastScrollEnd: true,
                            onRefresh: () => {
                                cacheHeights();
                            },
                        },
                    });

                    if (animation?.scrollTrigger) {
                        scrollTriggers.push(animation.scrollTrigger);
                    }
                });
            } catch (error) {
                console.error('ScrollJacking: Error creating animation:', error);
            }
        }

        createAnimation();

        // Cleanup function
        return () => {
            scrollTriggers.forEach((st) => {
                if (st && typeof st.kill === 'function') {
                    st.kill(true);
                }
            });
            scrollTriggers = [];

            slides.forEach((slide) => {
                if (slide) {
                    gsap.set(slide, { clearProps: 'all' });
                }
            });
        };
    });

    // Mobile behavior
    mm.add(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`, () => {
        slides.forEach((slide) => {
            if (slide) {
                gsap.set(slide, { clearProps: 'all' });
            }
        });

        return () => {
            // Mobile cleanup
        };
    });

    // Return master cleanup function
    return {
        destroy() {
            try {
                if (mm && typeof mm.kill === 'function') {
                    mm.revert();
                    mm.kill();
                    mm = null;
                }

                slides.forEach((slide) => {
                    if (slide) {
                        gsap.set(slide, { clearProps: 'all' });
                    }
                });
            } catch (error) {
                console.error('ScrollJacking: Error during cleanup:', error);
            }
        },
        refresh() {
            try {
                if (ScrollTrigger) {
                    ScrollTrigger.refresh();
                }
            } catch (error) {
                console.error('ScrollJacking: Error refreshing ScrollTrigger:', error);
            }
        },
    };
};
